import { Application, Container, Graphics, Rectangle, Text } from 'pixi.js';
import { getShards, getUpgrades, COSTS, buyUpgrade, resetProgression } from './prestige';
import { ACH_DEFS, unlockedSet } from './achievements';
import { settings, saveSettings } from './settings';
import { TRAIL_SKINS, getOwnedTrails, buyTrail, getActiveTrailId, setActiveTrailId } from './trails';
import { applyVolumes, SFX } from './audio';
import { G } from './state';

export type SubScreen = 'upgrades' | 'achievements' | 'settings' | 'trails' | 'leaderboard' | null;

export class Screens {
  view: Container;
  private bg: Graphics;
  private title: Text;
  current: SubScreen = null;
  lbData: {
    rows: Array<{ score: number; date: string }>;
    mode: string;
    tab?: 'campaign' | 'endless' | 'daily';
    all?: { campaign: Array<{ score: number; date: string }>; endless: Array<{ score: number; date: string }>; daily: Array<{ score: number; date: string }> };
  } = { rows: [], mode: '' };
  private onBack: () => void;
  private app: Application;
  private resetClicks = 0;
  private resetFirst = 0;
  private justReset = false;

  constructor(app: Application, onBack: () => void) {
    this.app = app;
    this.view = new Container();
    this.view.visible = false;
    app.stage.addChild(this.view);
    this.bg = new Graphics();
    this.view.addChild(this.bg);
    this.title = new Text({
      text: '',
      style: {
        fontFamily: 'Courier New', fontSize: 48, fill: 0xffffff, fontWeight: 'bold',
        letterSpacing: 6,
        dropShadow: { color: 0x00ffcc, alpha: 0.85, distance: 0, angle: 0, blur: 14 } as any,
      },
    });
    this.title.anchor.set(0.5, 0);
    this.view.addChild(this.title);
    this.onBack = onBack;
  }

  open(s: SubScreen): void {
    this.current = s;
    this.view.visible = !!s;
    if (s) this.render();
  }

  close(): void {
    this.current = null;
    this.view.visible = false;
  }

  private render(): void {
    const W = G.W;
    const H = G.H;
    this.bg.clear();
    this.bg.rect(0, 0, W, H);
    this.bg.fill({ color: 0x02040c, alpha: 0.95 });
    // Cyan accent bar — flush left
    this.bg.rect(W * 0.05, 36, 3, H - 130);
    this.bg.fill({ color: 0x00ffcc, alpha: 0.7 });
    // Clear text + buttons children except title and bg
    while (this.view.children.length > 2) this.view.removeChildAt(2);

    this.title.anchor.set(0, 0);
    this.title.x = W * 0.05 + 18;
    this.title.y = 36;

    if (this.current === 'upgrades') this.renderUpgrades(W, H);
    else if (this.current === 'achievements') this.renderAchievements(W, H);
    else if (this.current === 'settings') this.renderSettings(W, H);
    else if (this.current === 'trails') this.renderTrails(W, H);
    else if (this.current === 'leaderboard') this.renderLeaderboard(W, H);

    // Back button — top-right corner to avoid overlapping content rows
    this.button(W - 168, 36, 144, 44, '‹ RETOUR', 0x00ffcc, () => this.onBack());
  }

  private button(x: number, y: number, w: number, h: number, label: string, color: number, fn: () => void, disabled = false): void {
    // Modern glass button matching title menu language
    const btn = new Container();
    btn.x = x + w / 2;
    btn.y = y + h / 2;
    this.view.addChild(btn);

    const bg = new Graphics();
    bg.roundRect(-w / 2, -h / 2, w, h, Math.min(8, h / 4));
    bg.fill({ color: 0x070716, alpha: disabled ? 0.45 : 0.88 });
    bg.stroke({ width: 1.5, color, alpha: disabled ? 0.25 : 0.55 });
    btn.addChild(bg);

    if (h >= 30) {
      // Top sheen
      const sheen = new Graphics();
      sheen.roundRect(-w / 2 + 2, -h / 2 + 2, w - 4, Math.min(10, h / 4), 4);
      sheen.fill({ color, alpha: disabled ? 0.04 : 0.10 });
      btn.addChild(sheen);

      // Left accent strip
      const chev = new Graphics();
      chev.roundRect(-w / 2 + 4, -h / 2 + 5, 3, h - 10, 1.5);
      chev.fill({ color, alpha: disabled ? 0.3 : 1 });
      btn.addChild(chev);
    }

    const t = new Text({
      text: label,
      style: {
        fontFamily: 'Courier New',
        fontSize: h >= 36 ? 16 : 14,
        fill: disabled ? 0x666666 : color,
        fontWeight: 'bold',
        letterSpacing: 2,
        dropShadow: { color: 0x000000, alpha: 0.85, distance: 2, angle: Math.PI / 4, blur: 3 },
      },
    });
    t.anchor.set(0.5, 0.5);
    btn.addChild(t);

    if (!disabled) {
      btn.eventMode = 'static';
      btn.cursor = 'pointer';
      btn.hitArea = new Rectangle(-w / 2, -h / 2, w, h);
      const apply = (h0: boolean): void => {
        bg.clear();
        bg.roundRect(-w / 2, -h / 2, w, h, Math.min(8, h / 4));
        bg.fill({ color: h0 ? 0x0d0d28 : 0x070716, alpha: h0 ? 0.95 : 0.88 });
        bg.stroke({ width: h0 ? 2 : 1.5, color, alpha: h0 ? 1 : 0.55 });
        btn.scale.set(h0 ? 1.04 : 1, h0 ? 1.04 : 1);
      };
      btn.on('pointerover', () => { apply(true); SFX.menuHover(); });
      btn.on('pointerout',  () => apply(false));
      btn.on('pointertap', () => { SFX.menuClick(); fn(); });
    }
  }

  private label(x: number, y: number, msg: string, color = 0xffffff, size = 16, anchor = 0): Text {
    const t = new Text({
      text: msg,
      style: { fontFamily: 'Courier New', fontSize: size, fill: color },
    });
    t.anchor.set(anchor, 0);
    t.x = x; t.y = y;
    this.view.addChild(t);
    return t;
  }

  // ── UPGRADES ───────────────────────────────────────────────────────────────
  private renderUpgrades(W: number, H: number): void {
    this.title.text = 'AMÉLIORATIONS';
    const shards = getShards();
    const u = getUpgrades();
    this.label(W / 2, 100, `ÉCLATS : ${shards}`, 0xffdd00, 22, 0.5);

    const items: Array<{ key: 'hull' | 'cap' | 'kinetic' | 'magnet' | 'phoenix'; lbl: string; desc: string }> = [
      { key: 'hull', lbl: 'COQUE', desc: '+5/+10/+20 PV max' },
      { key: 'cap', lbl: 'CAPACITEUR', desc: '+2/+5/+10 bouclier max' },
      { key: 'kinetic', lbl: 'CINÉTIQUE', desc: '×1.05/1.10/1.15 dégâts' },
      { key: 'magnet', lbl: 'AIMANT', desc: '×1.5/2.0 rayon de ramassage' },
      { key: 'phoenix', lbl: 'PHÉNIX', desc: 'Réanimation une fois par partie à 50% PV' },
    ];

    const colW = 700;
    const x0 = W / 2 - colW / 2;
    let y = 160;
    for (const it of items) {
      const lvl = u[it.key];
      const max = COSTS[it.key].length;
      const cost = lvl < max ? COSTS[it.key][lvl] : null;
      const can = cost !== null && shards >= cost;

      const row = new Graphics();
      row.roundRect(x0, y, colW, 56, 8);
      row.fill({ color: 0x070716, alpha: 0.85 });
      row.stroke({ width: 1.5, color: 0x4488ff, alpha: 0.45 });
      // Left accent strip
      row.roundRect(x0 + 6, y + 6, 3, 44, 1.5);
      row.fill({ color: 0x00ffcc, alpha: 0.9 });
      this.view.addChild(row);

      this.label(x0 + 16, y + 8, it.lbl, 0x00ffff, 18);
      this.label(x0 + 16, y + 32, it.desc, 0xaaaaaa, 13);
      // Level dots
      for (let i = 0; i < max; i++) {
        const d = new Graphics();
        d.circle(x0 + 360 + i * 18, y + 28, 6);
        d.fill({ color: i < lvl ? 0x00ff66 : 0x222244 });
        this.view.addChild(d);
      }
      // Buy button
      const btnX = x0 + colW - 150, btnY = y + 12, btnW = 130, btnH = 32;
      const lbl = cost === null ? 'MAX' : `ACHETER ${cost}`;
      const col = can ? 0x00ff66 : (cost === null ? 0x888888 : 0xff4444);
      this.button(btnX, btnY, btnW, btnH, lbl, col, () => {
        if (buyUpgrade(it.key)) this.render();
      }, !can || cost === null);
      y += 64;
    }

    // RESET PROGRESSION — 3 clicks within 5s
    const now = Date.now();
    if (this.resetClicks > 0 && now - this.resetFirst > 5000) {
      this.resetClicks = 0;
    }
    const remaining = 3 - this.resetClicks;
    const resetLbl = this.resetClicks === 0 ? 'RESET PROGRESSION'
      : `RESET (${remaining} clic${remaining > 1 ? 's' : ''})`;
    const resetCol = this.resetClicks === 0 ? 0x555566 : this.resetClicks === 1 ? 0xff8800 : 0xff2222;
    this.button(W / 2 - 130, y + 8, 260, 36, resetLbl, resetCol, () => {
      const t = Date.now();
      if (this.resetClicks === 0) this.resetFirst = t;
      if (t - this.resetFirst > 5000) { this.resetClicks = 1; this.resetFirst = t; }
      else this.resetClicks += 1;
      if (this.resetClicks >= 3) {
        resetProgression();
        this.resetClicks = 0;
        this.justReset = true;
      }
      this.render();
    });

    if (this.justReset) {
      this.justReset = false;
      this.label(W / 2, y + 52, 'PROGRESSION RESET', 0xffaa00, 18, 0.5);
      setTimeout(() => { if (this.current === 'upgrades') this.render(); }, 1800);
    }
  }

  // ── ACHIEVEMENTS ───────────────────────────────────────────────────────────
  private renderAchievements(W: number, _H: number): void {
    this.title.text = 'SUCCÈS';
    const unl = unlockedSet();
    const colW = Math.min(800, W - 80);
    const x0 = W / 2 - colW / 2;
    const cols = 2;
    const cellW = colW / cols - 8;
    let y = 110;
    for (let i = 0; i < ACH_DEFS.length; i++) {
      const def = ACH_DEFS[i];
      const isUnl = unl.has(def.id);
      const col = (i % cols);
      const row = Math.floor(i / cols);
      const cx = x0 + col * (cellW + 8);
      const cy = y + row * 64;
      const g = new Graphics();
      g.roundRect(cx, cy, cellW, 56, 8);
      g.fill({ color: 0x070716, alpha: isUnl ? 0.92 : 0.7 });
      g.stroke({ width: 1.5, color: isUnl ? 0xffcc00 : 0x444466, alpha: isUnl ? 0.9 : 0.4 });
      g.roundRect(cx + 6, cy + 6, 3, 44, 1.5);
      g.fill({ color: isUnl ? 0xffcc00 : 0x222244, alpha: isUnl ? 1 : 0.5 });
      this.view.addChild(g);
      this.label(cx + 14, cy + 8, isUnl ? `🏆 ${def.name}` : '???', isUnl ? 0xffcc00 : 0x666666, 16);
      this.label(cx + 14, cy + 30, isUnl ? def.desc : '— verrouillé —', isUnl ? 0xcccccc : 0x444444, 12);
    }
    this.label(W / 2, y + Math.ceil(ACH_DEFS.length / cols) * 64 + 12, `débloqués : ${unl.size} / ${ACH_DEFS.length}`, 0xffaa00, 14, 0.5);
  }

  // ── SETTINGS ───────────────────────────────────────────────────────────────
  private renderSettings(W: number, _H: number): void {
    this.title.text = 'RÉGLAGES';
    const colW = 720;
    const x0 = W / 2 - colW / 2;
    let y = 140;

    const sectionHeader = (title: string, accent: number): void => {
      const head = new Graphics();
      head.roundRect(x0, y, colW, 36, 8);
      head.fill({ color: 0x070716, alpha: 0.85 });
      head.stroke({ width: 1.5, color: accent, alpha: 0.55 });
      head.roundRect(x0 + 8, y + 6, 3, 24, 1.5);
      head.fill({ color: accent });
      this.view.addChild(head);
      const t = new Text({
        text: title,
        style: {
          fontFamily: 'Courier New', fontSize: 16, fill: 0xffffff, fontWeight: 'bold',
          letterSpacing: 4,
          dropShadow: { color: 0x000000, alpha: 0.8, distance: 2, angle: Math.PI / 4, blur: 2 } as any,
        },
      });
      t.anchor.set(0, 0.5);
      t.x = x0 + 24; t.y = y + 18;
      this.view.addChild(t);
      y += 48;
    };

    const slider = (label: string, get: () => number, set: (v: number) => void): void => {
      const rowH = 72;
      // Card backplate
      const card = new Graphics();
      card.roundRect(x0, y, colW, rowH, 10);
      card.fill({ color: 0x070716, alpha: 0.85 });
      card.stroke({ width: 1.5, color: 0x4488ff, alpha: 0.45 });
      card.roundRect(x0 + 8, y + 8, 3, rowH - 16, 1.5);
      card.fill({ color: 0x00ffcc });
      this.view.addChild(card);

      // Label
      const lblText = new Text({
        text: label,
        style: { fontFamily: 'Courier New', fontSize: 14, fill: 0xddeeff, fontWeight: 'bold', letterSpacing: 3 },
      });
      lblText.anchor.set(0, 0.5);
      lblText.x = x0 + 24; lblText.y = y + 22;
      this.view.addChild(lblText);

      // % readout right side — given dedicated column right of the ± buttons
      const pct = new Text({
        text: `${Math.round(get() * 100)}%`,
        style: { fontFamily: 'Courier New', fontSize: 22, fill: 0x00ffcc, fontWeight: 'bold', letterSpacing: 1 },
      });
      pct.anchor.set(1, 0.5);
      pct.x = x0 + colW - 24; pct.y = y + rowH / 2;
      this.view.addChild(pct);

      // Slider track + fill — narrower so right column has room for ± buttons + %
      const sx = x0 + 24, sw = colW - 280, sy = y + rowH - 24, sh = 12;
      const bar = new Graphics();
      bar.roundRect(sx, sy, sw, sh, 6);
      bar.fill({ color: 0x0a0a1c, alpha: 0.95 });
      bar.stroke({ width: 1, color: 0x335599, alpha: 0.6 });
      bar.roundRect(sx, sy, Math.max(0.001, sw * get()), sh, 6);
      bar.fill({ color: 0x00ffcc, alpha: 0.95 });
      this.view.addChild(bar);
      // Knob marker
      const knobX = sx + sw * get();
      const knob = new Graphics();
      knob.circle(knobX, sy + sh / 2, 9);
      knob.fill({ color: 0xffffff });
      knob.stroke({ width: 1.5, color: 0x00ffcc });
      this.view.addChild(knob);

      // Click-to-set
      bar.eventMode = 'static';
      bar.cursor = 'pointer';
      bar.hitArea = new Rectangle(sx - 4, sy - 8, sw + 8, sh + 16);
      bar.on('pointertap', (ev) => {
        const local = ev.global;
        const inv = bar.worldTransform.applyInverse(local);
        const v = Math.max(0, Math.min(1, (inv.x - sx) / sw));
        set(v); applyVolumes(); this.render();
      });

      // ±10 buttons — left of % readout
      this.button(x0 + colW - 240, y + rowH / 2 - 16, 36, 32, '−', 0xff8800, () => { set(Math.max(0, get() - 0.1)); applyVolumes(); this.render(); });
      this.button(x0 + colW - 196, y + rowH / 2 - 16, 36, 32, '+', 0xff8800, () => { set(Math.min(1, get() + 0.1)); applyVolumes(); this.render(); });

      y += rowH + 10;
    };

    // ── AUDIO section ──────────────────────────────────────────────
    sectionHeader('AUDIO', 0x00ffcc);
    slider('VOLUME PRINCIPAL', () => settings.master, (v) => { settings.master = v; saveSettings(); });
    slider('VOLUME EFFETS',    () => settings.sfx,    (v) => { settings.sfx = v;    saveSettings(); });
    slider('VOLUME MUSIQUE',   () => settings.music,  (v) => { settings.music = v;  saveSettings(); });

    y += 12;

    // ── TESTER LES SONS — preview each SFX category from the menu ─────
    sectionHeader('TESTER LES SONS', 0xffaa44);
    {
      const tests: Array<{ lbl: string; col: number; fn: () => void }> = [
        { lbl: 'PULSE',     col: 0x00ffff, fn: () => SFX.shootByWeapon('PULSE') },
        { lbl: 'LASER',     col: 0xff44ff, fn: () => SFX.shootByWeapon('LASER') },
        { lbl: 'PLASMA',    col: 0xffaa00, fn: () => SFX.shootByWeapon('PLASMA') },
        { lbl: 'BOMBE',     col: 0xff4400, fn: () => SFX.shootByWeapon('BOMBE') },
        { lbl: 'ANNEAU',    col: 0xffff44, fn: () => SFX.shootByWeapon('ANNEAU') },
        { lbl: 'RAYON',     col: 0xff2200, fn: () => SFX.shootByWeapon('RAYON DE MORT') },
        { lbl: 'GUIDÉ',     col: 0x44ddff, fn: () => SFX.shootByWeapon('GUIDÉ') },
        { lbl: 'CHAOS',     col: 0xff8844, fn: () => SFX.shootByWeapon('CHAOS') },
        { lbl: 'EXPLOSION', col: 0xffaa44, fn: () => SFX.boom() },
        { lbl: 'BEACON',    col: 0x00ddff, fn: () => SFX.beacon() },
        { lbl: 'BOSS ROAR', col: 0xff44ff, fn: () => SFX.bossRoar() },
        { lbl: 'PICKUP',    col: 0x44ff88, fn: () => SFX.pickupHull() },
      ];
      const bw = 110, bh = 36, bgap = 6;
      const cols = 6;
      tests.forEach((tt, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const bx = x0 + 8 + col * (bw + bgap);
        const by = y + row * (bh + bgap);
        this.button(bx, by, bw, bh, tt.lbl, tt.col, tt.fn);
      });
      const rows = Math.ceil(tests.length / cols);
      y += rows * (bh + bgap) + 12;
    }

    y += 4;

    // ── AFFICHAGE section ──────────────────────────────────────────
    sectionHeader('AFFICHAGE', 0xff8800);

    const rowH = 56;
    const card = new Graphics();
    card.roundRect(x0, y, colW, rowH, 10);
    card.fill({ color: 0x070716, alpha: 0.85 });
    card.stroke({ width: 1.5, color: 0x4488ff, alpha: 0.45 });
    card.roundRect(x0 + 8, y + 8, 3, rowH - 16, 1.5);
    card.fill({ color: 0xff8800 });
    this.view.addChild(card);

    const shakeLbl = new Text({
      text: 'SECOUSSE D’ÉCRAN',
      style: { fontFamily: 'Courier New', fontSize: 14, fill: 0xddeeff, fontWeight: 'bold', letterSpacing: 3 },
    });
    shakeLbl.anchor.set(0, 0.5);
    shakeLbl.x = x0 + 24; shakeLbl.y = y + rowH / 2;
    this.view.addChild(shakeLbl);

    this.button(x0 + colW - 140, y + 12, 116, 32, settings.shake ? 'ACTIVÉ' : 'DÉSACTIVÉ',
      settings.shake ? 0x00ff66 : 0xff4444,
      () => { settings.shake = !settings.shake; saveSettings(); this.render(); });
    y += rowH + 16;

    // ── DEV / TRICHE — DIEU ─────────────────────────────────────────
    const godCard = new Graphics();
    godCard.roundRect(x0, y, colW, rowH, 10);
    godCard.fill({ color: 0x070716, alpha: 0.85 });
    godCard.stroke({ width: 1.5, color: 0xffaa00, alpha: 0.55 });
    godCard.roundRect(x0 + 8, y + 8, 3, rowH - 16, 1.5);
    godCard.fill({ color: 0xffaa00 });
    this.view.addChild(godCard);

    const godLbl = new Text({
      text: 'MODE DIEU (DEV)',
      style: { fontFamily: 'Courier New', fontSize: 14, fill: 0xffeebb, fontWeight: 'bold', letterSpacing: 3 },
    });
    godLbl.anchor.set(0, 0.5);
    godLbl.x = x0 + 24; godLbl.y = y + rowH / 2;
    this.view.addChild(godLbl);

    const godSub = new Text({
      text: 'invincible · spéciaux illimités',
      style: { fontFamily: 'Courier New', fontSize: 10, fill: 0x886633, letterSpacing: 1.5 },
    });
    godSub.anchor.set(0, 0);
    godSub.x = x0 + 24; godSub.y = y + rowH / 2 + 6;
    this.view.addChild(godSub);

    this.button(x0 + colW - 140, y + 12, 116, 32, settings.godMode ? 'ACTIVÉ' : 'DÉSACTIVÉ',
      settings.godMode ? 0xffaa00 : 0xff4444,
      () => { settings.godMode = !settings.godMode; saveSettings(); this.render(); });
    y += rowH + 16;

    // Footnote
    const note = new Text({
      text: 'les réglages sont sauvegardés localement',
      style: { fontFamily: 'Courier New', fontSize: 11, fill: 0x6688aa, letterSpacing: 1.5 },
    });
    note.anchor.set(0.5, 0);
    note.x = W / 2; note.y = y;
    this.view.addChild(note);
  }

  // ── TRAILS ─────────────────────────────────────────────────────────────────
  private renderTrails(W: number, _H: number): void {
    this.title.text = 'TRAÎNÉES';
    const owned = getOwnedTrails();
    const active = getActiveTrailId();
    const shards = getShards();
    this.label(W / 2, 100, `ÉCLATS : ${shards}`, 0xffdd00, 18, 0.5);

    const colW = 700;
    const x0 = W / 2 - colW / 2;
    let y = 150;
    for (const t of TRAIL_SKINS) {
      const isOwned = owned.has(t.id);
      const isActive = t.id === active;
      const row = new Graphics();
      row.roundRect(x0, y, colW, 64, 10);
      row.fill({ color: 0x070716, alpha: isActive ? 0.95 : 0.85 });
      row.stroke({ width: isActive ? 2 : 1.5, color: isActive ? 0x00ff66 : (isOwned ? 0x4488ff : 0x333355), alpha: isActive ? 1 : 0.55 });
      // Left accent strip
      row.roundRect(x0 + 6, y + 8, 3, 48, 1.5);
      row.fill({ color: isActive ? 0x00ff66 : (isOwned ? 0x00ffcc : 0xff8800), alpha: isActive ? 1 : isOwned ? 0.85 : 0.7 });
      this.view.addChild(row);

      // Trail preview swatches
      const sx = x0 + 16, sy = y + 24;
      [t.outer, t.mid, t.core].forEach((c, i) => {
        const d = new Graphics();
        d.circle(sx + i * 24, sy, 10 - i * 2);
        d.fill({ color: c });
        this.view.addChild(d);
      });

      this.label(x0 + 100, y + 10, t.name, isOwned ? 0xffffff : 0x888888, 18);
      this.label(x0 + 100, y + 36, isOwned ? (isActive ? 'ÉQUIPÉE' : 'POSSÉDÉE') : `coût ${t.cost}`,
        isActive ? 0x00ff66 : isOwned ? 0xaaaaaa : 0xff8800, 13);

      const btnX = x0 + colW - 150, btnY = y + 16, btnW = 130, btnH = 32;
      if (!isOwned) {
        const can = shards >= t.cost;
        this.button(btnX, btnY, btnW, btnH, `ACHETER ${t.cost}`, can ? 0x00ff66 : 0xff4444,
          () => { if (buyTrail(t.id)) this.render(); }, !can);
      } else if (!isActive) {
        this.button(btnX, btnY, btnW, btnH, 'ÉQUIPER', 0x00ffff,
          () => { setActiveTrailId(t.id); this.render(); });
      } else {
        this.button(btnX, btnY, btnW, btnH, '✓ ACTIVE', 0x00ff66, () => {}, true);
      }
      y += 72;
    }
  }

  // ── CLASSEMENT ─────────────────────────────────────────────────────────────
  private renderLeaderboard(W: number, H: number): void {
    this.title.text = 'CLASSEMENT';
    const { rows, tab, all } = this.lbData;
    const x0   = W * 0.05 + 18;
    const colW = Math.min(520, W - x0 - 40);

    // ── Onglets sélecteur ─────────────────────────────────────────────────────
    const TABS: Array<{ lbl: string; key: 'campaign' | 'endless' | 'daily' }> = [
      { lbl: 'CAMPAGNE',     key: 'campaign' },
      { lbl: 'SANS FIN',    key: 'endless'  },
      { lbl: 'DÉFI DU JOUR', key: 'daily'   },
    ];
    const tabW = Math.min(Math.floor(colW / 3) - 6, 158);
    TABS.forEach((t, i) => {
      const active = (tab ?? 'campaign') === t.key;
      this.button(x0 + i * (tabW + 6), 85, tabW, 34, t.lbl, active ? 0xffdd00 : 0x4488ff, () => {
        this.lbData.tab  = t.key;
        this.lbData.rows = all?.[t.key] ?? [];
        this.lbData.mode = t.lbl;
        this.open('leaderboard');
      });
    });

    // ── Bloc héro #1 ─────────────────────────────────────────────────────────
    const top = rows[0];
    const heroH = 76;
    const heroBg = new Graphics();
    heroBg.roundRect(x0 - 12, 128, colW, heroH, 12);
    heroBg.fill({ color: 0x1a1200, alpha: 0.95 });
    heroBg.stroke({ width: 2, color: 0xffdd00, alpha: 0.85 });
    heroBg.roundRect(x0 - 12, 128, 5, heroH, 2);
    heroBg.fill({ color: 0xffdd00, alpha: 1 });
    this.view.addChild(heroBg);

    this.label(x0 + 10,  138, '★  1',                              0xffdd00, 13);
    this.label(x0 + 10,  158, top ? top.score.toString() : '—',    0xffffff, 28);
    this.label(x0 + colW - 120, 170, top ? top.date : '',          0x888866, 13);

    // ── Rangs 2–10 ───────────────────────────────────────────────────────────
    const medals = ['◆', '◆', '·', '·', '·', '·', '·', '·', '·'];
    const cols   = [0xcccccc, 0xcc8844, 0xaabbcc, 0xaabbcc, 0xaabbcc, 0xaabbcc, 0xaabbcc, 0xaabbcc, 0xaabbcc];
    const rowH   = Math.min(40, (H - 230) / 9);
    let y        = 218;

    for (let i = 0; i < 9; i++) {
      const r   = rows[i + 1];
      const col = cols[i];

      const card = new Graphics();
      card.roundRect(x0 - 12, y - 2, colW, rowH - 4, 6);
      card.fill({ color: 0x07071a, alpha: 0.80 });
      card.stroke({ width: 1, color: col, alpha: i < 2 ? 0.5 : 0.2 });
      this.view.addChild(card);

      const rank  = `${medals[i]}  ${(i + 2).toString().padStart(2, ' ')}`;
      const score = r ? r.score.toString().padStart(9, ' ') : '         —';
      const date  = r ? r.date : '';
      this.label(x0,               y + 4, rank,  col,                  13);
      this.label(x0 + 80,          y + 4, score, r ? col : 0x334455,   13);
      this.label(x0 + colW - 120,  y + 4, date,  0x6688aa,             12);
      y += rowH;
    }
  }
}
